<template>
  <div class="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
    <!-- Desktop table (hidden on mobile) -->
    <table class="hidden md:table min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead class="bg-gray-50 dark:bg-gray-900">
        <tr>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
          >
            Naam
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
          >
            Email
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
          >
            Rol
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
          >
            Teams
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
          >
            Status
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
          >
            Dashboard
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
          >
            Toegevoegd op
          </th>
          <th scope="col" class="relative px-6 py-3">
            <span class="sr-only">Acties</span>
          </th>
        </tr>
      </thead>
      <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
        <tr
          v-for="authUser in users"
          :key="authUser.id"
          class="hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="text-sm font-medium text-gray-900 dark:text-white">
              {{ authUser.name || '-' }}
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="text-sm text-gray-900 dark:text-gray-200">{{ authUser.email }}</div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <span
              class="px-2 py-1 text-xs font-medium rounded-full"
              :class="getRoleBadgeClass(authUser.role)"
            >
              {{ getRoleLabel(authUser.role) }}
            </span>
          </td>
          <td class="px-6 py-4">
            <template v-if="authUser.teams && authUser.teams.length > 0">
              <span
                v-for="team in authUser.teams"
                :key="team.simplicate_id"
                class="inline-block px-2 py-0.5 text-xs font-medium rounded bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 mr-1 mb-1"
              >
                {{ team.name }}
              </span>
            </template>
            <span v-else class="text-gray-400 dark:text-gray-500 text-sm">-</span>
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <span
              class="px-2 py-1 text-xs font-medium rounded-full"
              :class="
                authUser.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              "
            >
              {{ authUser.is_active ? 'Actief' : 'Inactief' }}
            </span>
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <span
              class="px-2 py-1 text-xs font-medium rounded-full"
              :class="
                authUser.dashboard_access
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-gray-100 text-gray-500'
              "
            >
              {{ authUser.dashboard_access ? 'Ja' : 'Nee' }}
            </span>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
            {{ formatDate(authUser.created_at) }}
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
            <div class="flex items-center justify-end gap-2">
              <button
                @click="$emit('edit', authUser)"
                class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                title="Bewerken"
              >
                <PencilIcon class="h-4 w-4" />
              </button>
              <button
                @click="$emit('delete', authUser)"
                class="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                title="Verwijderen"
                :disabled="isCurrentUser(authUser)"
                :class="{ 'opacity-50 cursor-not-allowed': isCurrentUser(authUser) }"
              >
                <TrashIcon class="h-4 w-4" />
              </button>
            </div>
          </td>
        </tr>
        <tr v-if="users.length === 0">
          <td colspan="8" class="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
            Geen gebruikers gevonden
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Mobile cards (visible only on mobile) -->
    <div class="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
      <div
        v-for="authUser in users"
        :key="authUser.id"
        class="p-4 hover:bg-gray-50 dark:hover:bg-gray-700"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-gray-900 dark:text-white truncate">
              {{ authUser.name || '-' }}
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {{ authUser.email }}
            </div>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
            <span
              class="px-2 py-1 text-xs font-medium rounded-full"
              :class="getRoleBadgeClass(authUser.role)"
            >
              {{ getRoleLabel(authUser.role) }}
            </span>
            <span
              class="px-2 py-1 text-xs font-medium rounded-full"
              :class="
                authUser.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              "
            >
              {{ authUser.is_active ? 'Actief' : 'Inactief' }}
            </span>
            <span
              v-if="authUser.dashboard_access"
              class="px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700"
            >
              Dashboard
            </span>
          </div>
        </div>
        <div v-if="authUser.teams && authUser.teams.length > 0" class="mt-2 flex flex-wrap gap-1">
          <span
            v-for="team in authUser.teams"
            :key="team.simplicate_id"
            class="inline-block px-2 py-0.5 text-xs font-medium rounded bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300"
          >
            {{ team.name }}
          </span>
        </div>
        <div class="mt-3 flex items-center justify-between">
          <div class="text-xs text-gray-500 dark:text-gray-400">
            Toegevoegd: {{ formatDate(authUser.created_at) }}
          </div>
          <div class="flex items-center gap-3">
            <button
              @click="$emit('edit', authUser)"
              class="p-2 text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md"
              title="Bewerken"
            >
              <PencilIcon class="h-5 w-5" />
            </button>
            <button
              @click="$emit('delete', authUser)"
              class="p-2 text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
              title="Verwijderen"
              :disabled="isCurrentUser(authUser)"
              :class="{ 'opacity-50 cursor-not-allowed': isCurrentUser(authUser) }"
            >
              <TrashIcon class="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="users.length === 0"
        class="px-4 py-12 text-center text-gray-500 dark:text-gray-400"
      >
        Geen gebruikers gevonden
      </div>
    </div>
  </div>
</template>

<script setup>
import { PencilIcon, TrashIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  users: {
    type: Array,
    required: true
  },
  currentUserEmail: {
    type: String,
    default: ''
  },
  availableRoles: {
    type: Array,
    required: true
  }
})

defineEmits(['edit', 'delete'])

const isCurrentUser = authUser => {
  return authUser.email === props.currentUserEmail
}

const getRoleLabel = role => {
  const found = props.availableRoles.find(r => r.value === role)
  return found ? found.label : role
}

const getRoleBadgeClass = role => {
  const found = props.availableRoles.find(r => r.value === role)
  return found ? found.color : 'bg-gray-100 text-gray-700'
}

const formatDate = dateString => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}
</script>
